import { useState, useEffect, useCallback } from 'react';
import { crmService } from '../../services/crmService';

const STAGES = [
  { key: 'identify',   label: 'Identify',   color: '#6B7280' },
  { key: 'qualify',    label: 'Qualify',    color: '#3B82F6' },
  { key: 'develop',    label: 'Develop',    color: '#8B5CF6' },
  { key: 'capture',    label: 'Capture',    color: '#F59E0B' },
  { key: 'propose',    label: 'Propose',    color: '#EA580C' },
  { key: 'negotiate',  label: 'Negotiate',  color: '#EF4444' },
  { key: 'won',        label: 'Won',        color: '#10B981' },
  { key: 'lost',       label: 'Lost',       color: '#374151' },
];

const OPP_ROLES = [
  'decision_maker', 'influencer', 'champion', 'technical_evaluator',
  'contracting_officer', 'program_manager', 'end_user', 'gatekeeper', 'blocker',
];
const contactLabel = (c) => `${c.rank_code || c.grade_code || ''} ${c.first_name || ''} ${c.last_name || ''}`.trim();

const formatCurrency = (v) => {
  if (!v) return '—';
  if (v >= 1e9) return `$${(v/1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v/1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v/1e3).toFixed(0)}K`;
  return `$${v}`;
};

const OppCard = ({ opp, onClick }) => (
  <div
    onClick={() => onClick(opp)}
    style={{
      background: '#0A0F1C', border: '1px solid #1E2D45', borderRadius: '6px',
      padding: '10px 12px', cursor: 'pointer', marginBottom: '6px',
      borderLeft: `3px solid ${STAGES.find(s => s.key === opp.stage)?.color || '#374151'}`,
    }}
    onMouseEnter={e => e.currentTarget.style.borderColor = '#1E2D45'}
    onMouseLeave={e => e.currentTarget.style.borderColor = '#1E2D45'}
  >
    <div style={{ fontWeight: '600', fontSize: '12px', color: '#F1F5F9', marginBottom: '2px' }}>
      {opp.name}
    </div>
    {opp.program_name && (
      <div style={{ fontSize: '10px', color: '#6B7280', marginBottom: '4px' }}>{opp.program_name}</div>
    )}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ color: '#22D3EE', fontWeight: '600', fontSize: '12px' }}>
        {formatCurrency(opp.value_usd)}
      </span>
      {opp.probability != null && (
        <span style={{ fontSize: '10px', color: '#94A3B8' }}>{opp.probability}% P(win)</span>
      )}
    </div>
    {opp.customer_name && (
      <div style={{ fontSize: '10px', color: '#4B5563', marginTop: '2px' }}>{opp.customer_name}</div>
    )}
    {opp.award_date && (
      <div style={{ fontSize: '10px', color: '#374151', marginTop: '4px' }}>
        Award: {new Date(opp.award_date).toLocaleDateString()}
      </div>
    )}
    {opp.subsidiary_name && (
      <div style={{ fontSize: '9px', color: '#374151', marginTop: '2px' }}>{opp.subsidiary_name}</div>
    )}
    {(opp.verticals || []).length > 0 && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '5px' }}>
        {opp.verticals.slice(0, 3).map(v => (
          <span key={v.id} style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '9px', background: '#22D3EE18', color: '#22D3EE', border: '1px solid #22D3EE44' }}>
            {v.name}
          </span>
        ))}
        {opp.verticals.length > 3 && (
          <span style={{ fontSize: '9px', color: '#6B7280', alignSelf: 'center' }}>+{opp.verticals.length - 3}</span>
        )}
      </div>
    )}
  </div>
);

const OppModal = ({ opp, onClose, onSuccess, organizations, subsidiaries, verticals }) => {
  const isEdit = !!opp?.id;
  const [form, setForm] = useState({
    name: '', program_name: '', budget_line_item: '', stage: 'identify',
    value_usd: '', probability: '', solicitation_date: '', proposal_due_date: '',
    award_date: '', fiscal_year: '', contract_vehicle: '', contract_type: '',
    bd_owner: '', capture_manager: '', subsidiary_id: '', customer_org_id: '',
    notes: '',
    ...opp,
  });
  const [verticalIds, setVerticalIds] = useState(() => (opp?.verticals || []).map(v => v.id));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Linked contacts (list of {id, first_name, last_name, title, org_acronym, role})
  const [contacts, setContacts] = useState(() => opp?.contacts || []);
  const [origContacts, setOrigContacts] = useState(() => opp?.contacts || []);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);

  // Linked Tusks (drive the value estimate) + the estimate itself.
  const [tusks, setTusks] = useState(() => opp?.tusks || []);
  const [allTusks, setAllTusks] = useState([]);
  const [estimate, setEstimate] = useState(null);

  const loadEstimate = useCallback(() => {
    if (!opp?.id) return;
    crmService.getOpportunityValueEstimate(opp.id).then(setEstimate).catch(() => setEstimate(null));
  }, [opp?.id]);

  // On edit, pull the opportunity's current contacts + linked Tusks (the list card omits them),
  // load the Tusk roster for the picker, and fetch the value estimate.
  useEffect(() => {
    if (!opp?.id) return;
    crmService.getOpportunity(opp.id)
      .then(full => { setContacts(full.contacts || []); setOrigContacts(full.contacts || []); setTusks(full.tusks || []); })
      .catch(() => {});
    crmService.getTechProjects().then(rows => setAllTusks(Array.isArray(rows) ? rows : (rows.projects || rows.data || []))).catch(() => {});
    loadEstimate();
  }, [opp?.id, loadEstimate]);

  const linkTusk = async (id) => {
    if (!id || !opp?.id) return;
    await crmService.linkTechProjectOpportunity(id, opp.id);
    const t = allTusks.find(x => x.id === id);
    setTusks(prev => [...prev, { id, name: t?.name, psc_codes: t?.psc_codes }]);
    setTimeout(loadEstimate, 300);
  };
  const unlinkTusk = async (id) => {
    if (!opp?.id) return;
    await crmService.unlinkTechProjectOpportunity(id, opp.id);
    setTusks(prev => prev.filter(t => t.id !== id));
    setTimeout(loadEstimate, 300);
  };

  // Debounced contact search for the attach picker.
  useEffect(() => {
    if (!contactSearch.trim()) { setContactResults([]); return; }
    const t = setTimeout(() => {
      crmService.getContacts({ search: contactSearch.trim(), limit: 8 })
        .then(rows => setContactResults(Array.isArray(rows) ? rows : (rows.contacts || rows.data || [])))
        .catch(() => setContactResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [contactSearch]);

  const addContact = (c) => {
    if (contacts.some(x => x.id === c.id)) return;
    setContacts(prev => [...prev, { id: c.id, first_name: c.first_name, last_name: c.last_name, title: c.title, org_acronym: c.org_acronym || c.organization_acronym, role: '' }]);
    setContactSearch(''); setContactResults([]);
  };
  const removeContact = (id) => setContacts(prev => prev.filter(c => c.id !== id));
  const setContactRole = (id, role) => setContacts(prev => prev.map(c => c.id === id ? { ...c, role } : c));

  const set = (f, v) => setForm(prev => ({ ...prev, [f]: v }));

  const toggleVertical = (id) => setVerticalIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { setError('Name required'); return; }
    try {
      setSaving(true);
      const payload = { ...form, value_usd: form.value_usd ? parseInt(form.value_usd) : null, probability: form.probability ? parseInt(form.probability) : null, fiscal_year: form.fiscal_year ? parseInt(form.fiscal_year) : null };
      const saved = isEdit ? await crmService.updateOpportunity(opp.id, payload) : await crmService.createOpportunity(payload);
      const oppId = isEdit ? opp.id : saved.id;
      const prev = isEdit ? (opp.verticals || []).map(v => v.id) : [];
      const toAdd = verticalIds.filter(id => !prev.includes(id));
      const toDrop = prev.filter(id => !verticalIds.includes(id));
      // Contacts diff: upsert new/role-changed links, remove dropped ones.
      const origById = new Map(origContacts.map(c => [c.id, c]));
      const curById = new Map(contacts.map(c => [c.id, c]));
      const contactUpserts = contacts.filter(c => {
        const o = origById.get(c.id);
        return !o || (o.role || '') !== (c.role || '');
      });
      const contactRemovals = origContacts.filter(c => !curById.has(c.id)).map(c => c.id);
      await Promise.all([
        ...toAdd.map(id => crmService.addOpportunityVertical(oppId, id)),
        ...toDrop.map(id => crmService.removeOpportunityVertical(oppId, id)),
        ...contactUpserts.map(c => crmService.addOpportunityContact(oppId, { contact_id: c.id, role: c.role || null })),
        ...contactRemovals.map(id => crmService.removeOpportunityContact(oppId, id)),
      ]);
      onSuccess?.();
      onClose();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const inp = { background: '#0A0F1C', border: '1px solid #1E2D45', color: '#F1F5F9', padding: '6px 10px', borderRadius: '4px', fontSize: '12px', width: '100%', boxSizing: 'border-box' };
  const lbl = { color: '#94A3B8', fontSize: '11px', display: 'block', marginBottom: '3px' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: '8px', width: '600px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', color: '#F1F5F9' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #1E2D45', display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: '14px', color: '#22D3EE' }}>{isEdit ? 'Edit Opportunity' : 'New Opportunity'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: '18px' }}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={lbl}>Opportunity Name *</label>
            <input style={inp} value={form.name} onChange={e => set('name', e.target.value)} required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px' }}>
            <div>
              <label style={lbl}>Program Name</label>
              <input style={inp} value={form.program_name || ''} onChange={e => set('program_name', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Budget Line Item</label>
              <input style={inp} value={form.budget_line_item || ''} onChange={e => set('budget_line_item', e.target.value)} placeholder="PE 0603XXX" />
            </div>
            <div>
              <label style={lbl}>Stage</label>
              <select style={inp} value={form.stage} onChange={e => set('stage', e.target.value)}>
                {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Subsidiary</label>
              <select style={inp} value={form.subsidiary_id || ''} onChange={e => set('subsidiary_id', e.target.value ? parseInt(e.target.value) : null)}>
                <option value="">— Select —</option>
                {subsidiaries.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Customer Organization</label>
              <select style={inp} value={form.customer_org_id || ''} onChange={e => set('customer_org_id', e.target.value ? parseInt(e.target.value) : null)}>
                <option value="">— Select —</option>
                {organizations.map(o => <option key={o.id} value={o.id}>{o.acronym || o.name}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Value (USD)</label>
              <input type="number" style={inp} value={form.value_usd || ''} onChange={e => set('value_usd', e.target.value)} placeholder="e.g. 25000000" />
              {estimate && estimate.suggested ? (
                <div onClick={() => set('value_usd', estimate.suggested)} title={`Range ${formatCurrency(estimate.low)}–${formatCurrency(estimate.high)} · ${estimate.reason}`}
                  style={{ marginTop: '4px', fontSize: '10px', color: estimate.basis === 'vertical' ? '#94A3B8' : '#22D3EE', cursor: 'pointer' }}>
                  💡 Suggested {formatCurrency(estimate.suggested)}{estimate.basis === 'vertical' ? ' (rough, from verticals)' : ''} — click to use
                </div>
              ) : (estimate && !estimate.suggested ? (
                <div style={{ marginTop: '4px', fontSize: '10px', color: '#6B7280' }}>
                  {estimate.reason}
                </div>
              ) : null)}
            </div>
            <div>
              <label style={lbl}>P(Win) %</label>
              <input type="number" min={0} max={100} style={inp} value={form.probability || ''} onChange={e => set('probability', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Contract Vehicle</label>
              <input style={inp} value={form.contract_vehicle || ''} onChange={e => set('contract_vehicle', e.target.value)} placeholder="IDIQ, OTA, SBIR…" />
            </div>
            <div>
              <label style={lbl}>Contract Type</label>
              <select style={inp} value={form.contract_type || ''} onChange={e => set('contract_type', e.target.value)}>
                <option value="">— Select —</option>
                {['CPFF','CPIF','CPAF','FFP','FPIF','T&M','LH','IDIQ','OTA','grant','cooperative_agreement']
                  .map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Award Date</label>
              <input type="date" style={inp} value={form.award_date || ''} onChange={e => set('award_date', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Proposal Due</label>
              <input type="date" style={inp} value={form.proposal_due_date || ''} onChange={e => set('proposal_due_date', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>BD Owner</label>
              <input style={inp} value={form.bd_owner || ''} onChange={e => set('bd_owner', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Capture Manager</label>
              <input style={inp} value={form.capture_manager || ''} onChange={e => set('capture_manager', e.target.value)} />
            </div>
          </div>
          <div style={{ marginTop: '10px' }}>
            <label style={lbl}>Technology Verticals</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {(verticals || []).map(v => {
                const on = verticalIds.includes(v.id);
                return (
                  <button key={v.id} type="button" onClick={() => toggleVertical(v.id)} style={{
                    padding: '3px 10px', borderRadius: '10px', fontSize: '10px', cursor: 'pointer',
                    background: on ? '#22D3EE18' : 'transparent',
                    color: on ? '#22D3EE' : '#6B7280',
                    border: `1px solid ${on ? '#22D3EE44' : '#1E2D45'}`,
                    fontWeight: on ? 600 : 400,
                  }}>
                    {v.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ marginTop: '10px' }}>
            <label style={lbl}>Linked Tusks <span style={{ color: '#6B7280', fontWeight: 400 }}>(drive the value estimate)</span></label>
            {!isEdit ? (
              <div style={{ fontSize: '11px', color: '#6B7280' }}>Save the opportunity first to link Tusks.</div>
            ) : (
              <>
                {tusks.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '6px' }}>
                    {tusks.map(t => (
                      <span key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#0A0F1C', border: '1px solid #1E2D45', borderRadius: '10px', padding: '3px 8px', fontSize: '11px', color: '#F1F5F9' }}>
                        {t.name}
                        <button type="button" onClick={() => unlinkTusk(t.id)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '13px', lineHeight: 1 }}>×</button>
                      </span>
                    ))}
                  </div>
                )}
                <select style={inp} value="" onChange={e => e.target.value && linkTusk(parseInt(e.target.value))}>
                  <option value="">+ Link a Tusk…</option>
                  {allTusks.filter(t => !tusks.some(x => x.id === t.id)).map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </>
            )}
          </div>
          <div style={{ marginTop: '10px' }}>
            <label style={lbl}>Contacts</label>
            {contacts.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '6px' }}>
                {contacts.map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#0A0F1C', border: '1px solid #1E2D45', borderRadius: '4px', padding: '4px 8px' }}>
                    <span style={{ flex: 1, fontSize: '12px', color: '#F1F5F9' }}>
                      {contactLabel(c)}
                      {c.title ? <span style={{ color: '#6B7280' }}> · {c.title}</span> : null}
                      {c.org_acronym ? <span style={{ color: '#22D3EE' }}> · {c.org_acronym}</span> : null}
                    </span>
                    <select style={{ ...inp, width: 'auto', padding: '3px 6px' }} value={c.role || ''} onChange={e => setContactRole(c.id, e.target.value)}>
                      <option value="">— role —</option>
                      {OPP_ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                    </select>
                    <button type="button" onClick={() => removeContact(c.id)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '15px', lineHeight: 1 }}>×</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ position: 'relative' }}>
              <input style={inp} value={contactSearch} onChange={e => setContactSearch(e.target.value)} placeholder="Search contacts to attach (name, title, email)…" />
              {contactResults.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5, background: '#0A0F1C', border: '1px solid #1E2D45', borderRadius: '4px', marginTop: '2px', maxHeight: '180px', overflowY: 'auto' }}>
                  {contactResults.filter(r => !contacts.some(c => c.id === r.id)).map(r => (
                    <div key={r.id} onClick={() => addContact(r)} style={{ padding: '5px 8px', fontSize: '12px', color: '#F1F5F9', cursor: 'pointer', borderBottom: '1px solid #111827' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#111827'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      {contactLabel(r)}
                      {r.title ? <span style={{ color: '#6B7280' }}> · {r.title}</span> : null}
                      {(r.org_acronym || r.organization_acronym) ? <span style={{ color: '#22D3EE' }}> · {r.org_acronym || r.organization_acronym}</span> : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div style={{ marginTop: '10px' }}>
            <label style={lbl}>Notes</label>
            <textarea style={{ ...inp, height: '80px', resize: 'vertical' }} value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
          </div>
          {error && <div style={{ color: '#EF4444', fontSize: '12px', marginTop: '6px' }}>{error}</div>}
        </form>
        <div style={{ padding: '10px 18px', borderTop: '1px solid #1E2D45', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '6px 14px', background: '#111827', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving} style={{ padding: '6px 14px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
            {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function OpportunityPipeline() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editOpp, setEditOpp] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [subsidiaryFilter, setSubsidiaryFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      crmService.getOpportunities({ subsidiary_id: subsidiaryFilter || undefined }),
      crmService.getOrganizations(),
      crmService.getSubsidiaries(),
      crmService.getVerticals(),
    ]).then(([opps, orgs, subs, verts]) => {
      setOpportunities(opps);
      setOrganizations(orgs);
      setSubsidiaries(subs);
      setVerticals(verts);
    }).catch(console.error).finally(() => setLoading(false));
  }, [subsidiaryFilter]);

  const byStage = STAGES.reduce((acc, s) => {
    acc[s.key] = opportunities.filter(o => o.stage === s.key);
    return acc;
  }, {});

  const totalPipelineValue = opportunities
    .filter(o => !['won','lost','no_bid','closed'].includes(o.stage))
    .reduce((sum, o) => sum + (parseInt(o.value_usd) || 0), 0);

  const reloadOpps = useCallback(() => {
    crmService.getOpportunities({ subsidiary_id: subsidiaryFilter || undefined })
      .then(setOpportunities).catch(console.error);
  }, [subsidiaryFilter]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#0A0F1C' }}>
      {/* Header */}
      <div style={{ padding: 'clamp(8px, 2vw, 12px) clamp(12px, 4vw, 16px)', borderBottom: '1px solid #1E2D45', display: 'flex', gap: '12px', alignItems: 'center', backgroundColor: '#111827', flexShrink: 0, flexWrap: 'wrap' }}>
        <select value={subsidiaryFilter} onChange={e => setSubsidiaryFilter(e.target.value)}
          style={{ background: '#111827', border: '1px solid #1E2D45', color: '#F1F5F9', padding: '6px', borderRadius: '4px', fontSize: '12px', flex: '1 1 150px', minWidth: '130px' }}>
          <option value="">All Subsidiaries</option>
          {subsidiaries.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <div style={{ fontSize: 'clamp(10px, 2vw, 11px)' }}>
            <span style={{ color: '#6B7280' }}>Pipeline: </span>
            <span style={{ color: '#22D3EE', fontWeight: 'bold' }}>{formatCurrency(totalPipelineValue)}</span>
          </div>
          <button onClick={() => { setEditOpp(null); setShowModal(true); }}
            style={{ padding: '6px 12px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
            + Opp
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8' }}>Loading…</div>
      ) : (
        <div style={{ flex: 1, overflowX: 'auto', display: 'flex', gap: '0', padding: '0' }}>
          {STAGES.filter(s => !['no_bid','closed'].includes(s.key)).map(stage => {
            const stageOpps = byStage[stage.key] || [];
            const stageValue = stageOpps.reduce((sum, o) => sum + (parseInt(o.value_usd) || 0), 0);
            return (
              <div key={stage.key} style={{ flex: '0 0 clamp(160px, 25vw, 240px)', borderRight: '1px solid #111827', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '10px 12px', borderBottom: '2px solid ' + stage.color, backgroundColor: '#111827', flexShrink: 0 }}>
                  <div style={{ color: stage.color, fontWeight: '600', fontSize: '12px' }}>{stage.label}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                    <span style={{ color: '#6B7280', fontSize: '10px' }}>{stageOpps.length} deals</span>
                    <span style={{ color: '#94A3B8', fontSize: '10px' }}>{formatCurrency(stageValue)}</span>
                  </div>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                  {stageOpps.map(opp => (
                    <OppCard key={opp.id} opp={opp} onClick={o => { setEditOpp(o); setShowModal(true); }} />
                  ))}
                  {stageOpps.length === 0 && (
                    <div style={{ color: '#374151', fontSize: '11px', textAlign: 'center', paddingTop: '20px' }}>No opportunities</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <OppModal
          opp={editOpp}
          organizations={organizations}
          subsidiaries={subsidiaries}
          verticals={verticals}
          onSuccess={reloadOpps}
          onClose={() => { setShowModal(false); setEditOpp(null); }}
        />
      )}
    </div>
  );
}
